"use client";

import { useEffect, useState } from "react";
import { useAccountStatus } from "./use-api.js";

interface UseAccountRequiredOptions {
  showModal?: boolean;
}

export const useAccountRequired = (options: UseAccountRequiredOptions = {}) => {
  const { showModal = true } = options;
  const [showAccountModal, setShowAccountModal] = useState(false);
  const { data: accountStatus, isLoading, error } = useAccountStatus();

  // Check if any accounts are configured
  // Default to true while loading/unknown to avoid flashing the modal incorrectly
  const hasAnyAccount = accountStatus
    ? (
        !!accountStatus.google?.primary ||
        !!accountStatus.google?.secondary ||
        !!accountStatus.microsoft ||
        !!accountStatus.wtcEmailCalendarBridgeConfigured
      )
    : true;

  // Show modal only when we definitively know there are no connected accounts
  // Don't show modal if there's an error - let the error be handled by the parent component
  useEffect(() => {
    if (!showModal) {
      setShowAccountModal(false);
      return;
    }
    if (
      !isLoading &&
      !error &&
      accountStatus &&
      !(
        !!accountStatus.google?.primary ||
        !!accountStatus.google?.secondary ||
        !!accountStatus.microsoft ||
        !!accountStatus.wtcEmailCalendarBridgeConfigured
      )
    ) {
      setShowAccountModal(true);
    } else if (error) {
      // If there's an error, don't show the account required modal
      setShowAccountModal(false);
    }
  }, [isLoading, error, accountStatus, showModal]);

  // Don't show modal while loading
  const shouldShowModal = showAccountModal && !isLoading;

  const closeModal = () => {
    setShowAccountModal(false);
  };

  return {
    hasAnyAccount,
    isLoading,
    error,
    showAccountModal: shouldShowModal,
    closeModal,
    accountStatus
  };
};
